import { useState, useMemo, useCallback, useEffect, useContext } from "react";
import styles from "./OrganisationEvidence.module.scss";
import {
  Autocomplete,
  Avatar,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  TextField,
} from "@mui/material";
import { EvidenceTable } from "../components/EvidenceTable/EvidenceTable";
import type {
  EvidenceVolunteer,
  ActivityLogEntry,
  SearchVolunteer,
  CreateActivityLogEntry,
  UpdateActivityLogEntry,
} from "../types/evidence";
import { activityLogService } from "../services/activityLogService";
import { AuthContext } from "../context/AuthContext";
import { useAlert } from "../context/AlertContext";
import { useTranslation } from "../i18n";

export const OrganisationEvidence = () => {
  const authContext = useContext(AuthContext);
  const currentUser = authContext?.currentUser;
  const organisationId = currentUser?.userId;
  const { setAlert } = useAlert();
  const { t } = useTranslation();

  const [activityLogs, setActivityLogs] = useState<ActivityLogEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<SearchVolunteer | null>(null);
  const [inputValue, setInputValue] = useState("");
  const [searchOptions, setSearchOptions] = useState<SearchVolunteer[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newActivityForm, setNewActivityForm] = useState({
    volunteer_id: "",
    volunteer_name: "",
    volunteer_email: "",
    activity_date: new Date().toISOString().split("T")[0],
    hours: "",
    activity_description: "",
    note: "",
  });

  // Fetch activity logs on mount
  useEffect(() => {
    if (organisationId) {
      fetchActivityLogs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organisationId]);

  const fetchActivityLogs = async () => {
    if (!organisationId) return;

    try {
      setLoading(true);
      const data = await activityLogService.getActivityLog(organisationId);
      setActivityLogs(data);
    } catch (error) {
      console.error("Error fetching activity logs:", error);
      setAlert(true, "error", t("evidence.alerts.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  // Group activities by volunteer
  const volunteersWithActivities = useMemo<EvidenceVolunteer[]>(() => {
    const grouped = new Map<string, ActivityLogEntry[]>();

    activityLogs.forEach((log) => {
      const key = log.volunteer_id || log.volunteer_name;
      if (!grouped.has(key)) {
        grouped.set(key, []);
      }
      grouped.get(key)!.push(log);
    });

    return Array.from(grouped.entries()).map(([_, activities]) => {
      const firstActivity = activities[0];
      return {
        volunteer_id: firstActivity.volunteer_id,
        volunteer_name: firstActivity.volunteer_name,
        volunteer_email: firstActivity.volunteer_email,
        activities: activities.sort(
          (a, b) =>
            new Date(b.activity_date).getTime() -
            new Date(a.activity_date).getTime(),
        ),
      };
    });
  }, [activityLogs]);

  // Search volunteers with debounce
  useEffect(() => {
    const timeoutId = setTimeout(async () => {
      if (inputValue.length >= 3) {
        try {
          setSearchLoading(true);
          const results = await activityLogService.searchVolunteers(inputValue);
          setSearchOptions(results);
        } catch (error) {
          console.error("Error searching volunteers:", error);
        } finally {
          setSearchLoading(false);
        }
      } else {
        setSearchOptions([]);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [inputValue]);

  const handleSelectVolunteer = (volunteer: SearchVolunteer | null) => {
    if (volunteer) {
      setNewActivityForm({
        ...newActivityForm,
        volunteer_id: volunteer.id,
        volunteer_name: [volunteer.first_name, volunteer.last_name]
          .filter(Boolean)
          .join(" "),
        volunteer_email: volunteer.email,
      });
      setAddDialogOpen(true);
      setSearchValue(null);
      setInputValue("");
    }
  };

  const handleAddActivity = async () => {
    if (!organisationId) return;

    const hours = parseFloat(newActivityForm.hours);
    if (isNaN(hours) || hours <= 0) {
      setAlert(true, "error", t("evidence.alerts.hoursPositive"));
      return;
    }

    try {
      const entry: CreateActivityLogEntry = {
        volunteer_id: newActivityForm.volunteer_id || undefined,
        volunteer_name: newActivityForm.volunteer_name,
        volunteer_email: newActivityForm.volunteer_email || undefined,
        activity_date: newActivityForm.activity_date,
        hours: hours,
        activity_description: newActivityForm.activity_description,
        note: newActivityForm.note || undefined,
      };

      await activityLogService.createActivityLog(organisationId, entry);
      await fetchActivityLogs();
      setAlert(true, "success", t("evidence.alerts.added"));
      setAddDialogOpen(false);
      setNewActivityForm({
        volunteer_id: "",
        volunteer_name: "",
        volunteer_email: "",
        activity_date: new Date().toISOString().split("T")[0],
        hours: "",
        activity_description: "",
        note: "",
      });
    } catch (error) {
      console.error("Error creating activity log:", error);
      setAlert(true, "error", t("evidence.alerts.addFailed"));
    }
  };

  const handleRemoveVolunteer = useCallback(
    async (volunteerId: string) => {
      if (!organisationId) return;

      const volunteer = volunteersWithActivities.find(
        (v) => (v.volunteer_id || v.volunteer_name) === volunteerId,
      );
      if (!volunteer) return;

      const confirmed = window.confirm(
        t("evidence.confirmRemoveVolunteer", { name: volunteer.volunteer_name }),
      );
      if (!confirmed) return;

      try {
        await Promise.all(
          volunteer.activities.map((activity) =>
            activityLogService.deleteActivityLog(organisationId, activity.id),
          ),
        );
        await fetchActivityLogs();
        setAlert(
          true,
          "success",
          t("evidence.alerts.volunteerRemoved"),
        );
      } catch (error) {
        console.error("Error removing volunteer:", error);
        setAlert(true, "error", t("evidence.alerts.volunteerRemoveFailed"));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [organisationId, volunteersWithActivities, setAlert, t],
  );

  const handleAddActivityForVolunteer = useCallback(
    (volunteerId: string) => {
      const volunteer = volunteersWithActivities.find(
        (v) => (v.volunteer_id || v.volunteer_name) === volunteerId,
      );
      if (volunteer) {
        setNewActivityForm({
          volunteer_id: volunteer.volunteer_id || "",
          volunteer_name: volunteer.volunteer_name,
          volunteer_email: volunteer.volunteer_email || "",
          activity_date: new Date().toISOString().split("T")[0],
          hours: "",
          activity_description: "",
          note: "",
        });
        setAddDialogOpen(true);
      }
    },
    [volunteersWithActivities],
  );

  const handleRemoveActivity = useCallback(
    async (_volunteerId: string, activityId: string) => {
      if (!organisationId) return;

      try {
        await activityLogService.deleteActivityLog(organisationId, activityId);
        await fetchActivityLogs();
        setAlert(true, "success", t("evidence.alerts.activityRemoved"));
      } catch (error) {
        console.error("Error removing activity:", error);
        setAlert(true, "error", t("evidence.alerts.activityRemoveFailed"));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [organisationId, setAlert, t],
  );

  const handleActivityChange = useCallback(
    (
      _volunteerId: string,
      activityId: string,
      field: keyof ActivityLogEntry,
      value: any,
    ) => {
      setActivityLogs((prev) =>
        prev.map((log) =>
          log.id === activityId ? { ...log, [field]: value } : log,
        ),
      );
    },
    [],
  );

  const handleEditActivity = useCallback(
    (_volunteerId: string, activityId: string) => {
      setActivityLogs((prev) =>
        prev.map((log) =>
          log.id === activityId ? { ...log, isEditing: true } : log,
        ),
      );
    },
    [],
  );

  const handleSaveActivity = useCallback(
    async (_volunteerId: string, activityId: string) => {
      if (!organisationId) return;

      const activity = activityLogs.find((log) => log.id === activityId);
      if (!activity) return;

      try {
        const update: UpdateActivityLogEntry = {
          volunteer_name: activity.volunteer_name,
          volunteer_email: activity.volunteer_email,
          activity_date: activity.activity_date,
          hours: activity.hours,
          activity_description: activity.activity_description,
          note: activity.note,
        };

        await activityLogService.updateActivityLog(
          organisationId,
          activityId,
          update,
        );
        await fetchActivityLogs();
        setAlert(true, "success", t("evidence.alerts.updated"));
      } catch (error) {
        console.error("Error updating activity:", error);
        setAlert(true, "error", t("evidence.alerts.updateFailed"));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [organisationId, activityLogs, setAlert, t],
  );

  const handleCancelEditActivity = useCallback(
    (_volunteerId: string, _activityId: string) => {
      fetchActivityLogs();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleExportCSV = useCallback(async () => {
    if (!organisationId) return;

    try {
      const blob = await activityLogService.exportActivityLog(organisationId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `evidence_${new Date().toISOString().split("T")[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setAlert(true, "success", t("evidence.alerts.exported"));
    } catch (error) {
      console.error("Error exporting CSV:", error);
      setAlert(true, "error", t("evidence.alerts.exportFailed"));
    }
  }, [organisationId, setAlert, t]);

  if (!organisationId) {
    return (
      <div className={styles.evidence}>
        <p>{t("evidence.orgOnly")}</p>
      </div>
    );
  }

  return (
    <div className={styles.evidence}>
      <Paper className={styles.searchBox}>
        <div className={styles.searchWrapper}>
          <Autocomplete
            value={searchValue}
            onChange={(_, newValue) => handleSelectVolunteer(newValue)}
            inputValue={inputValue}
            onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
            options={searchOptions}
            loading={searchLoading}
            filterOptions={(options) => options}
            getOptionLabel={(option) => {
              const name = [option.first_name, option.last_name]
                .filter(Boolean)
                .join(" ");
              return name ? `${name} (${option.email})` : option.email;
            }}
            renderOption={(props, option) => (
              <li {...props} key={option.id} className={styles.searchOption}>
                <Avatar sx={{ width: 32, height: 32 }}>
                  {option.first_name?.charAt(0)?.toUpperCase()}
                </Avatar>
                <div className={styles.optionInfo}>
                  <div className={styles.optionName}>
                    {option.first_name} {option.last_name}
                  </div>
                  <div className={styles.optionEmail}>{option.email}</div>
                </div>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("evidence.search")}
                placeholder={t("evidence.searchPlaceholder")}
                variant="outlined"
                size="small"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {searchLoading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            noOptionsText={
              inputValue.length < 3
                ? t("evidence.searchMinChars")
                : t("evidence.searchNoResults")
            }
            className={styles.autocomplete}
          />
          <Button variant="contained" onClick={() => setAddDialogOpen(true)}>
            {t("evidence.addActivity")}
          </Button>
        </div>
      </Paper>
      <Paper className={styles.tableBox}>
        {loading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "2rem",
            }}
          >
            <CircularProgress />
          </div>
        ) : volunteersWithActivities.length === 0 ? (
          <div className={styles.emptyState}>
            <p>{t("evidence.emptyTitle")}</p>
            <p>{t("evidence.emptyHint")}</p>
          </div>
        ) : (
          <EvidenceTable
            volunteers={volunteersWithActivities}
            onRemoveVolunteer={handleRemoveVolunteer}
            onAddActivity={handleAddActivityForVolunteer}
            onRemoveActivity={handleRemoveActivity}
            onActivityChange={handleActivityChange}
            onEditActivity={handleEditActivity}
            onSaveActivity={handleSaveActivity}
            onCancelEditActivity={handleCancelEditActivity}
            onExportCSV={handleExportCSV}
          />
        )}
      </Paper>

      {/* Dialog for adding an activity */}
      <Dialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("evidence.dialogTitle")}</DialogTitle>
        <DialogContent>
          <div className={styles.dialogForm}>
            <TextField
              label={t("evidence.volunteerName")}
              value={newActivityForm.volunteer_name}
              onChange={(e) =>
                setNewActivityForm({
                  ...newActivityForm,
                  volunteer_name: e.target.value,
                })
              }
              required
              fullWidth
              size="small"
              margin="normal"
            />
            <TextField
              label={t("evidence.volunteerEmail")}
              type="email"
              value={newActivityForm.volunteer_email}
              onChange={(e) =>
                setNewActivityForm({
                  ...newActivityForm,
                  volunteer_email: e.target.value,
                })
              }
              fullWidth
              size="small"
              margin="normal"
            />
            <TextField
              label={t("evidence.activityDate")}
              type="date"
              value={newActivityForm.activity_date}
              onChange={(e) =>
                setNewActivityForm({
                  ...newActivityForm,
                  activity_date: e.target.value,
                })
              }
              required
              fullWidth
              size="small"
              margin="normal"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label={t("evidence.hours")}
              type="number"
              value={newActivityForm.hours}
              onChange={(e) =>
                setNewActivityForm({
                  ...newActivityForm,
                  hours: e.target.value,
                })
              }
              required
              fullWidth
              size="small"
              margin="normal"
              inputProps={{ min: 0.01, step: 0.25 }}
            />
            <TextField
              label={t("evidence.description")}
              value={newActivityForm.activity_description}
              onChange={(e) =>
                setNewActivityForm({
                  ...newActivityForm,
                  activity_description: e.target.value,
                })
              }
              required
              fullWidth
              multiline
              rows={3}
              size="small"
              margin="normal"
              inputProps={{ maxLength: 500 }}
            />
            <TextField
              label={t("evidence.note")}
              value={newActivityForm.note}
              onChange={(e) =>
                setNewActivityForm({
                  ...newActivityForm,
                  note: e.target.value,
                })
              }
              fullWidth
              multiline
              rows={2}
              size="small"
              margin="normal"
              inputProps={{ maxLength: 500 }}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)} variant="outlined">
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleAddActivity}
            variant="contained"
            disabled={
              !newActivityForm.volunteer_name.trim() ||
              !newActivityForm.activity_date ||
              !newActivityForm.hours ||
              !newActivityForm.activity_description.trim()
            }
          >
            {t("evidence.add")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
